import { Context } from '../context/Context';
import { Camera } from '../camera';
import { FullscreenQuad } from './FullscreenQuad';
import { GBuffer } from './GBuffer';
import { ProceduralSky } from '../object/primitives/ProceduralSky';
import { DirectionalLight } from '../light';
import { CloudVolume } from '../scene/cloud-volume/CloudVolume';
import { vec3 } from 'gl-matrix';

export const MAX_CLOUD_VOLUMES = 8;
/** Capacity of the shared polygon-edge buffer. Must match `MAX_EDGES_TOTAL` in
 *  the cloud-volumes fragment shader. With 8 volumes, that's an average budget
 *  of 12 edges/volume — generous for typical authoring (most polygons have
 *  3–6 vertices). Volumes whose polygons exceed the remaining budget get
 *  truncated and warn-logged once. */
export const MAX_POLYGON_EDGES_TOTAL = 96;

const tmpAabbMin = vec3.create();
const tmpAabbMax = vec3.create();
let polygonOverflowWarned = false;

/**
 * Volumetric cloud volumes pass. Slotted between aerial perspective and
 * transparency. For each fragment, raymarches authored polygon-prism volumes
 * and composites the accumulated density via standard alpha blending against
 * whatever is in the target framebuffer.
 *
 * Composites via `gl.BLEND` (`SRC_ALPHA`, `ONE_MINUS_SRC_ALPHA`) instead of a
 * read-from-scratch + shader-side `mix()`. The latter requires blitting from
 * the target to an RGBA16F scratch, which silently fails when the target is
 * the RGBA8 canvas (asset-manager editor without TAA/bloom). Using the GL
 * blend pipeline sidesteps the format-match requirement entirely — the
 * fragment shader outputs `vec4(litColor, density)` and the GPU does the
 * compositing inline.
 *
 * Implementation notes:
 *   - Polygon SDF computed in-shader from per-edge uniforms (`uPolygonEdges`),
 *     domain-warped by 2-octave FBM for organic silhouettes.
 *   - Atmosphere matching: `_shared/atmosphere.glsl` sampled ONCE per fragment
 *     at scene distance — sufficient because cloud volumes are small relative
 *     to the km-scale atmosphere and the dominant signal is `rayDir`, which
 *     doesn't vary between volumes.
 *   - Fragment-loops up to MAX_CLOUD_VOLUMES (no tile binning — at 8 volumes
 *     the per-non-hit-volume cost is ~6 ops, negligible vs raymarch).
 *   - Pass skipped entirely when scene.cloudVolumes is empty.
 */
export class CloudVolumePass {
  init(_ctx: Context): void {
    // No persistent GPU resources — the pass is purely a draw call against
    // whatever target the renderer hands in. Kept as a method to match the
    // sibling-pass interface and so future versions (SDF texture array,
    // tile-binning UBO) have a hook.
  }

  render(
    ctx: Context,
    gBuffer: GBuffer,
    quad: FullscreenQuad,
    camera: Camera,
    volumes: readonly CloudVolume[],
    sky: ProceduralSky | undefined,
    sunLight: DirectionalLight | undefined,
    elapsedMs: number,
    outputBuffer: WebGLFramebuffer | null,
  ): void {
    const gl = ctx.gl;

    gl.bindFramebuffer(gl.FRAMEBUFFER, outputBuffer);
    gl.viewport(0, 0, ctx.viewPort.width, ctx.viewPort.height);
    gl.disable(gl.DEPTH_TEST);
    // Premultiplied-alpha blend: `out = src.rgb + dst.rgb * (1 - src.a)`. The
    // shader's `lit` accumulator is already density-weighted via the per-step
    // `(1 - trans) * (1 - density_so_far)` loop, so it's effectively
    // `cloud_color * density`. Using premultiplied means we don't multiply by
    // density a second time at the GL stage.
    gl.enable(gl.BLEND);
    gl.blendFunc(gl.ONE, gl.ONE_MINUS_SRC_ALPHA);

    const shader = ctx.engine.shaderManager.getCloudVolumesShader();
    ctx.setShader(shader);

    // Sun direction: convention is "toward the sun". Mirrors AerialPerspectivePass.
    const sunDir = vec3.create();
    if (sunLight) {
      sunDir[0] = -sunLight.direction[0];
      sunDir[1] = -sunLight.direction[1];
      sunDir[2] = -sunLight.direction[2];
    } else if (sky) {
      sunDir[0] = sky.sunDirection[0];
      sunDir[1] = sky.sunDirection[1];
      sunDir[2] = sky.sunDirection[2];
    } else {
      sunDir[1] = 1; // fallback: straight up
    }
    vec3.normalize(sunDir, sunDir);

    // Atmosphere matching: pull all five sky parameters when available so
    // cloud colour stays in lock-step with the dome behind it through
    // day-night transitions. Fallbacks match ProceduralSky's defaults so
    // scenes without a procedural sky still render a sensible neutral cloud.
    const cloudColor = sky?.cloudColor ?? [1.0, 1.0, 1.0];
    const fogColor = sky?.fogColor ?? [0.7, 0.78, 0.85];
    const tintColor = sky?.tintColor ?? [1.0, 1.0, 1.0];
    const sunIntensity = sky?.sunIntensity ?? 12.0;
    const horizonMist = sky?.horizonMist ?? 0.25;
    const exposure = sky?.exposure ?? 1.5;

    const count = Math.min(volumes.length, MAX_CLOUD_VOLUMES);
    const u = shader.uniformsContainer
      .setDepthTexture(gBuffer.depthTexture)
      .setInvViewProjection(camera.invViewProjection)
      .setCameraPosition(camera.cameraPosition as number[])
      .setTime(elapsedMs * 0.001)
      .setFogColor(fogColor as number[])
      .setCloudColor(cloudColor as number[])
      .setTintColor(tintColor as number[])
      .setSunIntensity(sunIntensity)
      .setHorizonMist(horizonMist)
      .setExposure(exposure)
      .setSunDirection(sunDir as unknown as number[])
      .setVolumeCount(count);

    let edgeCursor = 0;
    for (let i = 0; i < count; i++) {
      const v = volumes[i];
      v.getWorldAabb(tmpAabbMin, tmpAabbMax);
      // Pad the XZ AABB so noise-displaced silhouette wisps extending past
      // the polygon get raymarched. Must match the shader's
      // `silhouetteAmp = max(edgeFeather * 3, 25)` plus a margin for the
      // SDF feather itself; `edgeFeather * 4 + 35` covers both terms with
      // headroom and avoids per-frame branching.
      const xzPad = v.edgeFeather * 4 + 35;
      u.setVolumeMin(i, [tmpAabbMin[0] - xzPad, tmpAabbMin[1], tmpAabbMin[2] - xzPad, v.baseY]);
      u.setVolumeMax(i, [tmpAabbMax[0] + xzPad, tmpAabbMax[1], tmpAabbMax[2] + xzPad, v.topY]);
      u.setVolumeParams(i, [v.density, v.coverage, v.edgeFeather, v.noiseScale]);
      u.setVolumeWind(i, [v.windDir[0], v.windDir[1], v.windSpeed, 0]);

      // Pack polygon edges into the shared edge buffer. Each edge = vec4
      // (vert.x, vert.y, nextVert.x, nextVert.y). Polygon is closed, so the
      // last edge wraps from the last vertex back to the first.
      const polygon = v.polygon;
      const polyN = polygon.length;
      const remaining = MAX_POLYGON_EDGES_TOTAL - edgeCursor;
      const edgeCount = Math.min(polyN, remaining);
      if (polyN > remaining && !polygonOverflowWarned) {
        console.warn(
          `[CloudVolumePass] polygon edge budget exhausted; volume "${v.name}" truncated to ${edgeCount}/${polyN} edges. Increase MAX_POLYGON_EDGES_TOTAL.`,
        );
        polygonOverflowWarned = true;
      }
      u.setVolumePolygonStart(i, edgeCursor);
      u.setVolumePolygonCount(i, edgeCount);
      for (let k = 0; k < edgeCount; k++) {
        const a = polygon[k];
        const b = polygon[(k + 1) % polyN];
        u.setPolygonEdges(edgeCursor + k, [a[0], a[1], b[0], b[1]]);
      }
      edgeCursor += edgeCount;
    }

    shader.validate();
    quad.draw(gl);

    // Restore default GL state for the next pass (transparency expects depth
    // test + its own blend func).
    gl.disable(gl.BLEND);
  }

  destroy(_gl: WebGL2RenderingContext): void {
    // Nothing to release — this pass owns no GPU resources.
  }
}
